import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import ListingCard from "../components/ListingCard";

const selectCls =
  "border border-engraved bg-white px-4 py-2.5 text-xs font-medium text-ink outline-none transition-colors focus:border-navy";

const Listings = () => {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [beds, setBeds] = useState("");
  const [price, setPrice] = useState("");
  const [q, setQ] = useState("");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusTabs = [
    { value: "all", label: t("ls.all"), testid: "filter-all-button" },
    { value: "for_sale", label: t("ls.forSale"), testid: "filter-for-sale-button" },
    { value: "sold", label: t("ls.sold"), testid: "filter-sold-button" },
  ];

  const priceRanges = [
    { value: "", label: t("ls.anyPrice") },
    { value: "0-400000", label: t("ls.under400") },
    { value: "400000-500000", label: "$400K – $500K" },
    { value: "500000-", label: "$500K +" },
  ];

  useEffect(() => {
    const tm = setTimeout(() => {
      const params = {};
      if (status !== "all") params.status = status;
      if (beds) params.min_beds = Number(beds);
      if (price) {
        const [min, max] = price.split("-");
        if (min) params.min_price = Number(min);
        if (max) params.max_price = Number(max);
      }
      if (q.trim()) params.q = q.trim();
      setLoading(true);
      api.get("/listings", { params })
        .then(({ data }) => setListings(data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(tm);
  }, [status, beds, price, q]);

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-8" data-testid="listings-page">
      <p className="eyebrow">{t("ls.eyebrow")}</p>
      <h1 className="mt-4 font-serif text-3xl font-bold tracking-tight text-navy sm:text-5xl">
        {t("ls.h1")}
      </h1>
      <p className="mt-4 max-w-lg text-base text-slate-600">
        {t("ls.sub")}
      </p>

      <div className="mt-10 flex flex-wrap items-center gap-3 border-y border-engraved py-5">
        <div className="flex border border-engraved">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              data-testid={tab.testid}
              onClick={() => setStatus(tab.value)}
              className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                status === tab.value ? "bg-navy text-paper" : "bg-white text-ink hover:text-crimson"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <select data-testid="filter-beds-select" value={beds} onChange={(e) => setBeds(e.target.value)} className={selectCls}>
          <option value="">{t("ls.anyBeds")}</option>
          {[2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}{t("ls.bedsSuffix")}</option>)}
        </select>
        <select data-testid="filter-price-select" value={price} onChange={(e) => setPrice(e.target.value)} className={selectCls}>
          {priceRanges.map((p) => <option key={p.label} value={p.value}>{p.label}</option>)}
        </select>
        <input
          data-testid="listings-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("ls.searchPh")}
          className="min-w-[200px] flex-1 border border-engraved bg-white px-4 py-2.5 text-xs text-ink outline-none transition-colors focus:border-navy"
        />
      </div>

      <p data-testid="listings-count" className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
        {loading ? t("ls.loading") : `${listings.length} ${listings.length === 1 ? t("ls.residence") : t("ls.residences")}`}
      </p>

      {loading ? (
        <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] animate-pulse bg-paper-cream" />)}
        </div>
      ) : listings.length === 0 ? (
        <div data-testid="listings-empty" className="mt-8 border border-engraved bg-white p-16 text-center">
          <p className="font-serif text-2xl font-bold text-navy">{t("ls.emptyTitle")}</p>
          <p className="mt-3 text-sm text-slate-600">
            {t("ls.emptyBody")} <a href="tel:+17022414761" className="text-crimson">702·241·4761</a>
          </p>
        </div>
      ) : (
        <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
        </div>
      )}
    </div>
  );
};

export default Listings;
